import type { League, LeagueSquad } from '../types/database';

/**
 * Digest used by the AdminPage red banner. Aggregates every squad in every
 * tennis league whose `unresolvedPlayerWarnings` array is non-empty.
 *
 * The recompute path writes those warnings whenever a squad player can't be
 * resolved by id OR canonical name in the current pool — see
 * `recomputeSquadFromPool` in `tennisMatchPerformance.ts`. The banner turns
 * "field in the database" into "thing the admin actually sees."
 */

export interface UnresolvedSquadEntry {
  squadId: string;
  squadName: string;
  warnings: string[];
}

export interface UnresolvedLeagueEntry {
  leagueName: string;
  squads: UnresolvedSquadEntry[];
}

export interface UnresolvedSquadDigest {
  totalSquads: number;
  totalWarnings: number;
  perLeague: UnresolvedLeagueEntry[];
}

export function summarizeUnresolvedSquadWarnings(
  bundle: { league: League; squads: LeagueSquad[] }[],
): UnresolvedSquadDigest {
  const perLeague: UnresolvedLeagueEntry[] = [];
  let totalSquads = 0;
  let totalWarnings = 0;

  for (const { league, squads } of bundle) {
    const affected = squads
      .filter((s) => (s.unresolvedPlayerWarnings?.length ?? 0) > 0)
      .map<UnresolvedSquadEntry>((s) => ({
        squadId: s.id,
        squadName: s.squadName,
        warnings: s.unresolvedPlayerWarnings ?? [],
      }));
    if (affected.length === 0) continue;
    affected.sort((a, b) => a.squadName.localeCompare(b.squadName));
    totalSquads += affected.length;
    totalWarnings += affected.reduce((sum, s) => sum + s.warnings.length, 0);
    perLeague.push({ leagueName: league.name, squads: affected });
  }
  perLeague.sort((a, b) => a.leagueName.localeCompare(b.leagueName));
  return { totalSquads, totalWarnings, perLeague };
}
